import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';

import {
  RIBBON_DURATION_MS,
  RIBBON_EASING,
  RIBBON_PHONE,
  ribbonPhoneKeyframes,
  RIBBON_PHONE_STEP_MS,
} from '@/components/atoms/ribbonMotion';
import { company } from '@/config/company';
import { callablePhones } from '@/utils/phoneChannels';

import { PhoneIcon } from './icons';

/**
 * The numbers a visitor can actually ring.
 *
 * The same selector the utility strip applies, and for the same reason: the
 * Japan line in `config/company.ts` is a sourcing desk, not a sales one, and
 * a visitor who dialled it from Kampala reached the wrong half of the
 * business. It is published on /contact, in the footer and in the mobile
 * drawer as the WhatsApp line it is — surfaces with room to say so. A ribbon
 * that appears on scroll has no room for that explanation, so it carries only
 * the lines that need none.
 *
 * It filters on `channels` now rather than on `country === 'Uganda'`. The
 * country was never the rule; it only happened to pick out the same two
 * numbers. See `utils/phoneChannels.ts`.
 */
const ribbonPhones = callablePhones;

/**
 * One part of the row, and when it arrives.
 *
 * `both` fill for the same reason the message's parts use it: before its
 * delay each part holds the keyframe's `from`, after the run it holds `to`,
 * and removing the animation — which is what the reduced-motion rule does —
 * leaves the part in its natural state with nothing to undo.
 */
const part = (index: number) =>
  ({
    animation: `${RIBBON_PHONE} ${RIBBON_DURATION_MS}ms ${RIBBON_EASING} ${index * RIBBON_PHONE_STEP_MS}ms 1 both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }) as const;

/**
 * The phone numbers, for the ribbon's left column once the strip has folded.
 *
 * This is the other half of a handover that was only ever built in one
 * direction. The utility strip holds two things — the numbers on the left,
 * the socials on the right — and collapses on scroll; the socials had a
 * destination in the ribbon and the numbers did not, so a visitor who
 * scrolled lost the phone number and kept the Facebook icon. That is the
 * wrong one to lose on a site whose conversion is a phone call, and it is
 * what left the ribbon's first column empty while its third was full.
 *
 * A separate component from `RibbonSocialLinks` rather than a variant of it,
 * on the same grounds that one is separate from the footer's `SocialIconLinks`:
 * these are text links at a resting opacity of 1, those are translucent
 * glyphs that pulse. Nothing but the brand-red surface is shared.
 *
 * **Resting opacity is 1, and that is a contrast decision rather than a
 * stylistic one.** The icons opposite sit at 0.85 because an icon is a
 * non-text graphic and answers to 3:1. These are 13px text and answer to
 * 4.5:1 — `primary.contrastText` on `primary.main` is 5.08:1 in the light
 * scheme, but the same white dimmed to 0.85 composites to 4.09:1 and fails.
 * There is no room to dim text on this bar; the hierarchy against the promise
 * is carried by size and weight instead.
 *
 * Which numbers show is CSS, not JavaScript, for the reason the message
 * states: the site is prerendered, so a `matchMedia` decision here would bake
 * one width's answer into the HTML served to every visitor.
 */
export function RibbonPhoneLinks() {
  if (ribbonPhones.length === 0) return null;

  const [primary, ...rest] = ribbonPhones;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 2,
        minWidth: 0,
        // The row is one line at every width it appears at; it may not wrap
        // into the message beside it, and it may not grow the bar.
        whiteSpace: 'nowrap',
        fontSize: '0.8125rem',
        // A step below the promise's 14px on purpose. The bar's job is the
        // sentence in the middle; these are the convenience either side of it,
        // and the only hierarchy available is size, since dimming is out.
        fontWeight: 600,
        lineHeight: 1.2,
        [`@keyframes ${RIBBON_PHONE}`]: ribbonPhoneKeyframes,
      }}
    >
      <Box component="span" sx={[part(0), { display: 'inline-flex', flexShrink: 0 }]}>
        <PhoneIcon aria-hidden sx={{ fontSize: 16 }} />
      </Box>

      <Box
        component={Link}
        href={`tel:${primary.e164}`}
        // Contains the visible text, so it satisfies label-in-name (2.5.3)
        // while telling a screen-reader user what the digits are *for* —
        // which the bare number, read out of context at the top of a page,
        // does not.
        aria-label={`Call ${company.brandName} on ${primary.display}`}
        underline="none"
        sx={[
          part(1),
          { color: 'inherit', '&:hover, &:focus-visible': { textDecoration: 'underline' } },
        ]}
      >
        {primary.display}
      </Box>

      {rest.map((phone, index) => (
        <Box
          key={phone.e164}
          component="span"
          sx={{
            // The second line is xl-and-up only, and that is measured rather
            // than chosen. The pair measures 250px against the 118px one
            // number costs, and at lg the bar is already carrying a 725px
            // message — promise, proof clause and the `Start my import` link
            // — plus 171px of controls in 1,152px of usable width. 118 fits
            // there with room to spare and 250 does not; xl is the first
            // width where the second number is a choice rather than a
            // squeeze.
            display: { xs: 'none', xl: 'inline-flex' },
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 3,
              height: 3,
              borderRadius: '50%',
              bgcolor: 'currentColor',
              opacity: 0.55,
              flexShrink: 0,
            }}
          />
          <Box
            component={Link}
            href={`tel:${phone.e164}`}
            aria-label={`Call ${company.brandName} on ${phone.display}`}
            underline="none"
            sx={[
              part(index + 2),
              { color: 'inherit', '&:hover, &:focus-visible': { textDecoration: 'underline' } },
            ]}
          >
            {phone.display}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
